import { useState, useRef, useEffect } from 'react';

/**
 * ColFilter — Excel-style dropdown filter on a table <th>.
 *
 * Props:
 *   label        – column header text
 *   options      – array of { value, label } OR array of strings
 *   value        – current selected filter value ('' = all)
 *   onChange     – (newValue) => void
 *   width        – optional dropdown width (default 180px)
 */
export function ColFilter({ label, options = [], value, onChange, width = 180 }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  const active = value && value !== '';
  const normalised = options.map(o => typeof o === 'string' ? { value: o, label: o } : o);
  const filtered = search
    ? normalised.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : normalised;

  return (
    <div className="col-filter-wrap" ref={ref}>
      <button
        className={`col-filter-btn ${active ? 'col-filter-btn--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={`Filter by ${label}`}
      >
        {label}
        <span className={`col-filter-arrow ${open ? 'col-filter-arrow--open' : ''}`}>▾</span>
        {active && <span className="col-filter-dot" />}
      </button>

      {open && (
        <div className="col-filter-dropdown" style={{ minWidth: width, maxHeight: 320, overflowY: 'auto' }}>
          <input
            className="col-filter-search"
            type="text"
            placeholder={`Search ${label}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', marginBottom: 6, padding: '2px 6px', fontSize: '0.95em', boxSizing: 'border-box' }}
            autoFocus
          />
          <div
            className={`col-filter-option ${!value ? 'col-filter-option--active' : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            <span className="col-filter-check">{!value ? '✓' : ''}</span>
            All
          </div>
          {filtered.map(o => (
            <div
              key={o.value}
              className={`col-filter-option ${value === o.value ? 'col-filter-option--active' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              <span className="col-filter-check">{value === o.value ? '✓' : ''}</span>
              {o.label}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-filter-option col-filter-option--disabled" style={{ color: '#aaa', cursor: 'default' }}>
              No results
            </div>
          )}
        </div>
      )}
    </div>
  );
}
